import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, type NotificationPrefs, type PerTypePref } from '../../services/api'
import { matchesStored } from './storedValue'
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Chip,
  ChipGroup,
  Field,
  Hint,
  Icon,
  Inline,
  LoadingState,
  SaveBar,
  Section,
  SettingRow,
  Stack,
  Toggle,
} from '../../ds'

interface NotificationsSectionProps {
  onClose: () => void
}

type Mode = 'shadow' | 'assisted'
type NotifType = 'digest_item' | 'alert' | 'escalation'

// Mirror of the dispatcher's mode defaults — what applies when a row is unset.
const TYPE_DEFAULTS: Record<Mode, Partial<Record<NotifType, PerTypePref>>> = {
  shadow: {
    digest_item: { enabled: true, delivery: 'aggregated', cadence: 'daily', sendHourLocal: 8, sendDayLocal: 'mon', channels: ['dashboard', 'email'] },
  },
  assisted: {
    escalation: { enabled: true, delivery: 'immediate', cadence: 'daily', sendHourLocal: 8, sendDayLocal: 'mon', channels: ['dashboard', 'email'] },
    alert: { enabled: true, delivery: 'aggregated', cadence: 'daily', sendHourLocal: 8, sendDayLocal: 'mon', channels: ['dashboard'] },
    digest_item: { enabled: false, delivery: 'aggregated', cadence: 'weekly', sendHourLocal: 8, sendDayLocal: 'mon', channels: ['email'] },
  },
}

const TYPE_LABELS: Record<NotifType, { title: string; hint: string }> = {
  escalation: { title: 'Approvals', hint: 'Decisions held for your sign-off — always sent the moment they happen' },
  alert: { title: 'Action alerts', hint: 'Things Marva did that are worth knowing about' },
  digest_item: { title: 'Everything digest', hint: 'A rolled-up summary of all activity' },
}

const HOURS = Array.from({ length: 24 }, (_, h) => h)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const DEFAULT_PREFS: NotificationPrefs = { timezone: null, shadow: {}, assisted: {}, slackWebhookUrl: null, emailRecipients: [] }

/**
 * The ONE place this form becomes a PUT body — `handleSave` sends it and the dirty check
 * compares it against the stored row (D-50, `storedValue.ts`), so the two cannot disagree.
 */
const buildNotificationsPayload = (
  prefs: NotificationPrefs,
  recipientsText: string,
  stored: NotificationPrefs | null
): NotificationPrefs => ({
  ...prefs,
  // "No webhook" is written the way the stored row already says it. The GET of a row that never
  // had one OMITS the key (`slackWebhookUrl` is `.nullish()` with no default), while a row saved
  // without one holds `null` — and `matchesStored` counts `null` against absent as a write. Always
  // writing `null` made every never-configured workspace open on "Unsaved changes". The two mean
  // the same to the server (the PUT replaces the whole row), so mirroring the stored form is free.
  slackWebhookUrl: prefs.slackWebhookUrl || (stored?.slackWebhookUrl === null ? null : undefined),
  emailRecipients: recipientsText.split(',').map((s) => s.trim()).filter(Boolean),
})

const CollapsibleCard = ({
  title,
  subtitle,
  children,
  defaultOpen = false,
}: {
  title: string
  subtitle: string
  children: React.ReactNode
  defaultOpen?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  return (
    <Card>
      <CardHeader
        title={title}
        description={subtitle}
        aside={
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={isOpen}
            icon={<Icon d={isOpen ? 'M6 9l6 6 6-6' : 'M9 6l6 6-6 6'} />}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? 'Hide' : 'Show'}
            <span className="mv-sr"> {title}</span>
          </Button>
        }
      />
      {isOpen && <div>{children}</div>}
    </Card>
  )
}

/** One per-type preference row: enable, delivery style, cadence + hour, channels. */
const TypePrefRow = ({
  type,
  pref,
  timezone,
  onChange,
}: {
  type: NotifType
  pref: PerTypePref
  /** The merchant's stored timezone, when there is one — the hour is local to it. */
  timezone?: string | null
  onChange: (patch: Partial<PerTypePref>) => void
}) => {
  const isEscalation = type === 'escalation' // approvals are always immediate — batching would delay a held action
  const label = TYPE_LABELS[type]
  const toggleChannel = (ch: 'email' | 'slack') => {
    const has = pref.channels.includes(ch)
    onChange({ channels: has ? pref.channels.filter((c) => c !== ch) : [...pref.channels, ch] })
  }
  const val = (e: { target: unknown }) => (e.target as HTMLSelectElement).value
  return (
    <SettingRow
      title={label.title}
      description={label.hint}
      aside={
        // A draft in this form, written by Save below.
        <Toggle checked={pref.enabled} onChange={() => onChange({ enabled: !pref.enabled })} label={`Enable ${label.title}`} labelHidden />
      }
    >
      {/* Controls, then where it goes, each on its own line: side by side, the chips wrapped under
          some rows and not others, so Approvals' and Action alerts' never lined up. */}
      {pref.enabled && (
        <Stack gap="sm">
          <Inline>
            {!isEscalation && (
              <Field as="select" aria-label={`${label.title}: delivery`} value={pref.delivery} onChange={(e) => onChange({ delivery: val(e) as PerTypePref['delivery'] })}>
                <option value="immediate">One by one</option>
                <option value="aggregated">Rolled into a digest</option>
              </Field>
            )}
            {isEscalation && <Hint as="span">Sent immediately (approvals can’t wait for a digest)</Hint>}
            {!isEscalation && pref.delivery === 'aggregated' && (
              <>
                <Field as="select" aria-label={`${label.title}: how often`} value={pref.cadence} onChange={(e) => onChange({ cadence: val(e) as PerTypePref['cadence'] })}>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </Field>
                {pref.cadence === 'weekly' && (
                  <Field as="select" aria-label={`${label.title}: day`} value={pref.sendDayLocal} onChange={(e) => onChange({ sendDayLocal: val(e) as PerTypePref['sendDayLocal'] })}>
                    {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((d) => (
                      <option key={d} value={d}>{d.toUpperCase()}</option>
                    ))}
                  </Field>
                )}
                {pref.cadence !== 'hourly' && (
                  // A select of whole hours, worded as a time: a bare number box between "at" and
                  // ":00" did not read as one. The stored value is still the hour as a number.
                  <Field
                    as="select"
                    aria-label={`${label.title}: send at hour`}
                    value={String(pref.sendHourLocal)}
                    onChange={(e) => onChange({ sendHourLocal: parseInt(val(e), 10) })}
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>at {String(h).padStart(2, '0')}:00</option>
                    ))}
                  </Field>
                )}
                {pref.cadence !== 'hourly' && timezone && <Hint as="span">{timezone}</Hint>}
              </>
            )}
          </Inline>
          <ChipGroup label={`${label.title}: where`}>
            <Chip tone="gold" title="Always shown on your dashboard">Dashboard</Chip>
            <Chip kind="select" selected={pref.channels.includes('email')} onToggle={() => toggleChannel('email')}>
              Email
            </Chip>
            <Chip kind="select" selected={pref.channels.includes('slack')} onToggle={() => toggleChannel('slack')}>
              Slack
            </Chip>
          </ChipGroup>
        </Stack>
      )}
    </SettingRow>
  )
}

/**
 * How the last read of the stored preferences ended. `loading` is never stored: it is what any
 * workspace without a settled read of its own reads as (see `readStatus`).
 *
 * ⚠️ `empty` and `failed` must never collapse. `empty` is a fact — nothing stored, so the defaults
 * on screen are something to save (D-50). `failed` is an unknown — a Save from defaults would
 * clear the merchant's real Slack webhook and recipients.
 */
type ReadStatus = 'loading' | 'ok' | 'empty' | 'failed'

const NotificationsSection = ({ onClose }: NotificationsSectionProps) => {
  void onClose
  const { currentWorkspace } = useWorkspaceStore()
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS)
  // ⚠️ The "Store knowledge" answers editor that lived here MOVED to Knowledge → Your answers
  // (`components/knowledge/MerchantAnswers.tsx`, MAR-325), with every MAR-323 state. One home:
  // this section is delivery preferences only.
  const [read, setRead] = useState<{ workspaceId: string; status: Exclude<ReadStatus, 'loading'> } | null>(null)
  // Bumped by "Try again" to re-run the read for the same workspace.
  const [readAttempt, setReadAttempt] = useState(0)
  // Scoped at the read: a settled read belongs only to the workspace that produced it.
  const readStatus: ReadStatus =
    read && read.workspaceId === currentWorkspace?.id ? read.status : 'loading'
  const [isUpdating, setIsUpdating] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [recipientsText, setRecipientsText] = useState('')
  /**
   * The row as STORED — the raw read, or the body last successfully saved; `null` when nothing
   * was read (no row, or a failed read — `readStatus` tells them apart, and only the former makes
   * the defaults dirty). D-50: dirty is "what Save would write differs from this", never "the form differs
   * from what it displayed" — the display merges `DEFAULT_PREFS` over a partial row, and those
   * filled-in defaults are something Save would write.
   */
  const [stored, setStored] = useState<NotificationPrefs | null>(null)
  // The workspace on screen NOW (set by the load effect), so a save that resolves after a
  // switch lands nothing under B.
  const workspaceIdRef = useRef(currentWorkspace?.id)

  useEffect(() => {
    if (!currentWorkspace?.id) return
    let cancelled = false
    // ⚠️ Reset FIRST: a failed or empty read for this workspace must never leave the previous
    // workspace's preferences on screen, let alone as clean.
    const asked = currentWorkspace.id
    workspaceIdRef.current = asked
    setRead(null)
    setPrefs(DEFAULT_PREFS)
    setRecipientsText('')
    setStored(null)
    setMessage(null)
    setIsUpdating(false)
    governanceApi.getNotificationPrefs(asked)
      .then((prefsRes) => {
        if (cancelled) return
        // ⚠️ A refusal RESOLVES (`apiClient.request` returns `{ success: false }`, it never
        // rejects), so this branch — not the catch — is what a 500, a 403 or a network failure
        // reaches. It is an unknown, never "nothing stored".
        if (!prefsRes.success) { setRead({ workspaceId: asked, status: 'failed' }); return }
        if (!prefsRes.data) { setRead({ workspaceId: asked, status: 'empty' }); return }
        const merged = { ...DEFAULT_PREFS, ...prefsRes.data }
        setPrefs(merged)
        setRecipientsText((merged.emailRecipients || []).join(', '))
        setStored(prefsRes.data)
        setRead({ workspaceId: asked, status: 'ok' })
      })
      .catch(() => { if (!cancelled) setRead({ workspaceId: asked, status: 'failed' }) })
    return () => { cancelled = true }
  }, [currentWorkspace?.id, readAttempt])

  const effectivePref = (mode: Mode, type: NotifType): PerTypePref => ({
    ...(TYPE_DEFAULTS[mode][type] as PerTypePref),
    ...((prefs[mode]?.[type] || {}) as Partial<PerTypePref>),
  })

  const patchPref = (mode: Mode, type: NotifType, patch: Partial<PerTypePref>) =>
    setPrefs((p) => ({ ...p, [mode]: { ...p[mode], [type]: { ...effectivePref(mode, type), ...patch } } }))

  const validate = (): string | null => {
    const recipients = recipientsText.split(',').map((s) => s.trim()).filter(Boolean)
    if (recipients.some((r) => !EMAIL_RE.test(r))) return 'Recipients must be valid email addresses, comma-separated.'
    if (prefs.slackWebhookUrl && !/^https:\/\//.test(prefs.slackWebhookUrl)) return 'Slack webhook must be an https:// URL.'
    for (const mode of ['shadow', 'assisted'] as Mode[]) {
      for (const type of Object.keys(prefs[mode] || {}) as NotifType[]) {
        const h = effectivePref(mode, type).sendHourLocal
        if (!Number.isInteger(h) || h < 0 || h > 23) return 'Send hour must be a whole hour between 0 and 23.'
      }
    }
    return null
  }

  const handleSave = async () => {
    if (!currentWorkspace?.id) return
    // Only after a settled read: from `failed` (or mid-read) the form holds defaults, and a Save
    // would write them over preferences this screen could not see.
    if (readStatus !== 'ok' && readStatus !== 'empty') return
    const err = validate()
    if (err) { setMessage(err); return }
    const savedFor = currentWorkspace.id
    try {
      setIsUpdating(true)
      setMessage(null)
      const payload = buildNotificationsPayload(prefs, recipientsText, stored)
      const res = await governanceApi.saveNotificationPrefs(savedFor, payload)
      if (workspaceIdRef.current !== savedFor) return
      if (!res.success) throw new Error(res.error || 'Failed to save notification preferences')
      setPrefs(payload)
      setRecipientsText(payload.emailRecipients.join(', '))
      setStored(payload)
      setMessage('Notification preferences saved.')
    } catch (e) {
      if (workspaceIdRef.current !== savedFor) return
      setMessage(e instanceof Error ? e.message : 'Failed to save notification preferences')
    } finally {
      if (workspaceIdRef.current === savedFor) setIsUpdating(false)
    }
  }

  if (readStatus === 'loading') return <Card><LoadingState caption="Loading notifications…" /></Card>

  const readFailed = readStatus === 'failed'

  const isError = message != null && !message.includes('saved')
  // D-50: dirty when what Save would write differs from what is stored. A read that stored
  // nothing leaves `stored` null, so the defaults on screen are unsaved — which they are. A read
  // that FAILED leaves it null too, but there the defaults are not an edit: Save is blocked.
  const dirty = !readFailed && !matchesStored(buildNotificationsPayload(prefs, recipientsText, stored), stored)
  const barState = isUpdating ? 'saving' : dirty ? 'dirty' : message && !isError ? 'saved' : 'clean'

  return (
    <Section
      title="Notifications"
      description={
        <>
          How you hear about what Marva does — approvals the moment they’re needed, everything else on your schedule.
          Open approvals and customer questions live under the <Link to="/notifications">bell</Link>; the answers
          you’ve given are in <Link to="/knowledge?scope=answers">Knowledge → Your answers</Link>.
        </>
      }
    >
      <Stack>
        {readFailed && (
          <Alert
            tone="danger"
            live="assertive"
            actions={
              <Button size="sm" variant="ghost" onClick={() => setReadAttempt((n) => n + 1)}>
                Try again
              </Button>
            }
          >
            She couldn’t read your notification preferences just now — nothing is lost. Try again before saving:
            the settings below are defaults, not yours.
          </Alert>
        )}
        <CollapsibleCard
          title="While reviewing (shadow mode)"
          subtitle="Marva isn’t sending anything yet — choose how you see what she would have done"
          defaultOpen={currentWorkspace?.mode === 'shadow'}
        >
          <TypePrefRow timezone={prefs.timezone} type="digest_item" pref={effectivePref('shadow', 'digest_item')} onChange={(p) => patchPref('shadow', 'digest_item', p)} />
        </CollapsibleCard>

        <CollapsibleCard
          title="While live"
          subtitle="Approvals always come through immediately; choose what else you want and how often"
          defaultOpen={currentWorkspace?.mode !== 'shadow'}
        >
          <TypePrefRow timezone={prefs.timezone} type="escalation" pref={effectivePref('assisted', 'escalation')} onChange={(p) => patchPref('assisted', 'escalation', p)} />
          <TypePrefRow timezone={prefs.timezone} type="alert" pref={effectivePref('assisted', 'alert')} onChange={(p) => patchPref('assisted', 'alert', p)} />
          <TypePrefRow timezone={prefs.timezone} type="digest_item" pref={effectivePref('assisted', 'digest_item')} onChange={(p) => patchPref('assisted', 'digest_item', p)} />
        </CollapsibleCard>

        <CollapsibleCard
          title="Where to reach you"
          subtitle="Email recipients and an optional Slack webhook"
        >
          <Stack gap="sm">
            <Field
              label="Email recipients (comma-separated)"
              placeholder="you@store.com, ops@store.com"
              value={recipientsText}
              onChange={(e) => setRecipientsText((e.target as HTMLInputElement).value)}
            />
            <Field
              label="Slack webhook URL"
              optional
              type="url"
              placeholder="https://hooks.slack.com/services/…"
              value={prefs.slackWebhookUrl || ''}
              onChange={(e) => {
                const v = (e.target as HTMLInputElement).value
                setPrefs((p) => ({ ...p, slackWebhookUrl: v || null }))
              }}
            />
          </Stack>
        </CollapsibleCard>

        {message && (
          <Alert tone={isError ? 'danger' : 'success'} live={isError ? 'assertive' : 'polite'}>
            {message}
          </Alert>
        )}

        <SaveBar
          state={barState}
          note="Changes apply from her next notification."
          blocked={readFailed}
          blockedNote="Read your preferences first — saving now would replace them with these defaults."
          saveLabel="Save notification preferences"
          onSave={handleSave}
        />
      </Stack>
    </Section>
  )
}

export default NotificationsSection
